import { Link, Navigate } from "react-router-dom";
import { User, Mail, Building2, Briefcase, UserPlus } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { useRegister } from "../hooks/useRegister";
import InputField from "../../../shared/components/form/InputField";
import PasswordField from "../components/PasswordField";
import PhoneField from "../../../shared/components/form/PhoneField";
import { getExistsFieldError } from "../utils/validators";
import { useLanguage } from "../../../context/LanguageContext";

export default function RegisterClient() {
  // Hooks must come before any early returns (Rules of Hooks)
  const { user } = useAuth();
  const { t } = useLanguage();
  const { formData, isLoading, error, fieldErrors, phoneExists, checkingPhone, emailExists, checkingEmail, handleChange, handleSubmit, setField } =
    useRegister({ role: "client" });

  const emailError = getExistsFieldError(fieldErrors.email, checkingEmail, emailExists, t("auth.emailAlreadyRegistered"));

  if (user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-white via-gray-50 to-white flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#b3d130] rounded-full mb-4 shadow-md">
              <UserPlus size={32} className="text-[#313131]" />
            </div>
            <h1 className="text-3xl font-bold text-[#313131] mb-2">{t("auth.joinArteria")}</h1>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form className="space-y-5" onSubmit={(e) => handleSubmit(e, { company: formData.company, company_type: formData.company_type, title: formData.title })}>
            <InputField label={t("auth.firstName")} icon={User} required name="first_name" value={formData.first_name} onChange={handleChange} error={fieldErrors.first_name} placeholder={t("auth.firstNamePlaceholder")} />
            <InputField label={t("auth.lastName")} icon={User} required name="last_name" value={formData.last_name} onChange={handleChange} error={fieldErrors.last_name} placeholder={t("auth.lastNamePlaceholder")} />
            <InputField label={t("auth.emailAddress")} icon={Mail} required type="email" name="email" value={formData.email} onChange={handleChange} error={emailError} placeholder={t("auth.emailPlaceholder")} />
            {checkingEmail && <p className="text-xs text-gray-400 -mt-3">{t("auth.checkingEmail")}</p>}
            <PhoneField label={t("auth.phoneNumber")} required value={formData.phone ?? ""} onChange={(v) => setField("phone", v)} onCountryChange={(c) => setField("phone_country", c ?? "")} error={fieldErrors.phone} phoneExists={phoneExists} checkingPhone={checkingPhone} />
            <PhoneField label={t("auth.whatsappNumber")} name="whatsapp_number" value={formData.whatsapp_number ?? ""} onChange={(v) => setField("whatsapp_number", v)} error={fieldErrors.whatsapp_number} />
            <InputField label={t("auth.registerClient.companyName")} icon={Building2} required name="company" value={formData.company ?? ""} onChange={handleChange} error={fieldErrors.company} placeholder={t("auth.registerClient.companyNamePlaceholder")} />
            <InputField label={t("auth.registerClient.companyType")} icon={Building2} name="company_type" value={formData.company_type ?? ""} onChange={handleChange} error={fieldErrors.company_type} placeholder={t("auth.registerClient.companyTypePlaceholder")} />
            <InputField label={t("auth.registerClient.yourTitle")} icon={Briefcase} name="title" value={formData.title ?? ""} onChange={handleChange} error={fieldErrors.title} placeholder={t("auth.registerClient.titlePlaceholder")} />
            <PasswordField label={t("auth.password")} name="password" value={formData.password} onChange={handleChange} error={fieldErrors.password} placeholder={t("auth.passwordPlaceholder")} />
            <PasswordField label={t("auth.confirmPassword")} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} error={fieldErrors.confirmPassword} placeholder={t("auth.confirmPasswordPlaceholder")} />

            <button type="submit" disabled={isLoading || phoneExists || checkingPhone || emailExists || checkingEmail}
              className="w-full bg-gradient-to-r from-[#b3d130] to-[#a5c428] text-gray-900 py-4 rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? t("auth.registerClient.creatingAccount") : t("auth.registerClient.createAccount")}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link to="/login" className="text-[#b3d130] font-semibold hover:underline">{t("auth.signInLink")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
